import React from 'react';
import isEmpty from 'lodash.isempty';

import {
  Page,
  Text,
  View,
  Font,
  Document,
  StyleSheet,
} from '@react-pdf/renderer';

import {
  AssessmentAnswer,
  ProgramAssessmentDataFragment,
} from '../../../../generated/graphql';

import { isAssessmentAForm } from '../../../lib/assessments';
import { AssessmentSchemaType } from '../../../../generated/graphql';

import { colors } from '../../../lib/colors';
import { getFullDateTimeStamp } from '../../../lib/time';
import { ProgramAssessmentQuestion } from '../../../lib/program-assessment';
import { registerPdfFonts } from '../../../lib/registerPdfFonts';

import { getScoreProps } from '../../Assessments/helpers';

import Score from './components/Score';
import Header from './components/Header';
import ResponsesSection from './components/ResponsesSection';
import PdfFormattedText from '../../Library/LibraryFormPDF/components/PdfFormattedText';

registerPdfFonts(Font);

const ProgramAssessmentPDF: React.FC<{
  programAssessment: ProgramAssessmentDataFragment;
  isProviderUser: boolean;
}> = ({ programAssessment, isProviderUser }) => {
  const title = programAssessment?.assessment?.name;

  // Treat NonScored (e.g. OHA 303) like Custom for layout: no Score section, no
  // auto-numbered response rows. The PDF mirrors the on-screen slideover.
  const isNonScored =
    programAssessment?.assessment?.schemaType ===
    AssessmentSchemaType.NonScored;
  const isForm =
    isAssessmentAForm(programAssessment?.assessment) || isNonScored;

  const isPractitionerForm =
    programAssessment?.assessment?.completableByProviderOnly === true;
  const pa = programAssessment as ProgramAssessmentDataFragment & {
    completedByType?: string | null;
    completedByProviderUser?: { name?: string | null } | null;
    programAssessmentSignatureUsers?: Array<{
      signedAt?: unknown;
      providerUser?: { name?: string | null };
    }>;
  };
  const signatureProviderName = pa?.programAssessmentSignatureUsers?.find(
    (u) => u.signedAt,
  )?.providerUser?.name;
  const completedByProviderName =
    signatureProviderName ?? pa?.completedByProviderUser?.name;
  const completedByClient =
    isPractitionerForm && pa?.completedByType === 'patient';
  const completedByLabel = isPractitionerForm
    ? completedByProviderName
      ? `Completed by ${completedByProviderName}`
      : completedByClient
      ? 'Completed by Client'
      : null
    : null;

  const { primary, secondary } = getScoreProps(
    programAssessment,
    programAssessment.assessment.type,
  );

  const questions = programAssessment?.assessment?.questions as
    | ProgramAssessmentQuestion[]
    | undefined;

  const answers = programAssessment?.answers as AssessmentAnswer[] | undefined;

  const completedAtDateFormatted = getFullDateTimeStamp(
    programAssessment?.completedAt,
  );

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View>
          <Header
            patientName={programAssessment?.patientNameData?.name}
            completedAtDateFormatted={completedAtDateFormatted}
            completedByLabel={completedByLabel}
          />

          <Text
            style={{
              fontSize: 16,
              color: colors.neutral[150],
              fontFamily: 'serif',
              fontWeight: 400,
              marginBottom: 8,
            }}
          >
            {title}
          </Text>

          {!isForm && (
            <View>
              <Text style={styles.sectionTitle}>Score</Text>
              {!isEmpty(primary) && (
                <Score
                  score={primary.score}
                  categorization={primary.categorization}
                  color={primary.color}
                  hasPercentage={primary.hasPercentage}
                />
              )}
              {!isEmpty(secondary) && (
                <Score
                  score={secondary.score}
                  categorization={secondary.categorization}
                  color={secondary.color}
                  hasPercentage={secondary.hasPercentage}
                />
              )}
            </View>
          )}

          {programAssessment?.assessment.instructions && (
            <View>
              <Text style={styles.sectionTitle}>Instructions</Text>
              <PdfFormattedText
                style={{
                  fontSize: 10,
                  marginBottom: 4,
                  color: colors.neutral[125],
                  fontFamily: 'sans',
                  fontWeight: 400,
                }}
              >
                {programAssessment?.assessment.instructions}
              </PdfFormattedText>
            </View>
          )}

          <ResponsesSection
            questions={questions}
            answers={answers}
            isForm={isForm}
          />

          {programAssessment.assessment.description && isProviderUser && (
            <View>
              <Text style={styles.sectionTitle}>About</Text>
              <PdfFormattedText
                style={{
                  fontSize: 10,
                  lineHeight: 1,
                  color: colors.neutral[125],
                  fontFamily: 'sans',
                  fontWeight: 400,
                }}
              >
                {programAssessment.assessment.description}
              </PdfFormattedText>
            </View>
          )}
        </View>
      </Page>
    </Document>
  );
};

export default ProgramAssessmentPDF;

export const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontFamily: 'sans',
    backgroundColor: 'white',
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 500,
    color: colors.neutral[150],
    marginVertical: 12,
  },
  formQuestion: {
    marginBottom: 12,
    borderColor: colors.neutral[75],
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
  },
  assessmentQuestion: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  questionText: {
    fontSize: 12,
    color: colors.neutral[150],
    fontWeight: 400,
  },
  answerText: {
    fontSize: 12,
    color: colors.neutral[150],
    fontWeight: 500,
  },
  questionNumberContainer: {
    width: 20,
    marginRight: 12,
  },
  questionNumber: {
    fontSize: 12,
    color: colors.neutral[200],
    textAlign: 'right',
    fontWeight: 400,
  },
});
